import { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useNavigate, Link } from 'react-router-dom';
import { getInicioData, getDetalleCesiones, getCreditoLeasing, getTareasPendientes, getResumenFacturaCesion, getCesionesDistribucion } from '../api/apiConfig';
import { MdAttachMoney, MdTrendingUp, MdShowChart, MdCreditCard, MdDirectionsCar, MdAssignment, MdReceipt, MdBusiness, MdSwapHoriz, MdVisibility } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import * as XLSX from 'xlsx';
import { formatMoneyMM } from '../utils/numberFormat';
import { defaultHomePath } from '../utils/gestionCampanaAccess';

const Inicio = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const home = defaultHomePath(user);
    if (home && home !== '/inicio') {
      navigate(home, { replace: true });
    }
  }, [user, navigate]);
  const [inicioData, setInicioData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [creditoLeasingData, setCreditoLeasingData] = useState(null);
  const [tareasPendientes, setTareasPendientes] = useState([]);
  const [resumenData, setResumenData] = useState(null);
  const [cesionesDistribucion, setCesionesDistribucion] = useState([]);
  const [distribucionSortColumn, setDistribucionSortColumn] = useState(null);
  const [distribucionSortDirection, setDistribucionSortDirection] = useState('asc');
  const [distribucionSearchTerm, setDistribucionSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [creditoLeasingLoading, setCreditoLeasingLoading] = useState(true);
  const [tareasLoading, setTareasLoading] = useState(true);
  const [resumenLoading, setResumenLoading] = useState(true);
  const [distribucionLoading, setDistribucionLoading] = useState(true);

  const formatNumber = (num) => formatMoneyMM(num);

  const filterDistribucion = (dataToFilter) => {
    if (!distribucionSearchTerm.trim()) return dataToFilter;
    const searchLower = distribucionSearchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.CESIONARIO, item.FACTORING, item.MTO_CESION_MILL, item.PORCENTAJE_TOTAL];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedDistribucion = () => {
    let dataToSort = filterDistribucion(cesionesDistribucion);
    if (!distribucionSortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[distribucionSortColumn]; let bValue = b[distribucionSortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return distribucionSortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedDistribucion = getSortedDistribucion();

  const handleDistribucionSort = (column) => {
    if (distribucionSortColumn === column) setDistribucionSortDirection(distribucionSortDirection === 'asc' ? 'desc' : 'asc');
    else { setDistribucionSortColumn(column); setDistribucionSortDirection('asc'); }
  };

  const getDistribucionSortIcon = (column) => {
    if (distribucionSortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return distribucionSortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedDistribucion();
    const data = dataToExport.map(item => ({
      'Rut Factoring': item.CESIONARIO,
      'Factoring': item.FACTORING,
      'Monto de Cesiones (MM$)': item.MTO_CESION_MILL,
      'Porcentaje del Total (%)': (item.PORCENTAJE_TOTAL * 100).toFixed(2)
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Distribución de Cesiones');
    
    XLSX.writeFile(wb, `distribucion_cesiones_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      if (!user) {
        console.log('No user data available');
        return;
      }

      try {
        console.log('User data in Inicio:', user);
        
        // Obtener datos de las tarjetas
        const data = await getInicioData(user);
        if (isMounted) {
          setInicioData(data);
          setLoading(false);
        }
        
        // Obtener datos del gráfico
        const cesionesData = await getDetalleCesiones(user);
        if (isMounted) {
          setChartData(cesionesData || []);
          setChartLoading(false);
        }
        
        // Obtener datos de crédito y leasing
        const creditoData = await getCreditoLeasing(user);
        if (isMounted) {
          setCreditoLeasingData(creditoData);
          setCreditoLeasingLoading(false);
        }
        
        // Obtener tareas pendientes
        const tareasData = await getTareasPendientes(user);
        if (isMounted) {
          setTareasPendientes(tareasData || []);
          setTareasLoading(false);
        }
        
        // Obtener resumen de factura y cesión
        const resumenFacturaData = await getResumenFacturaCesion(user);
        if (isMounted) {
          setResumenData(resumenFacturaData);
          setResumenLoading(false);
        }
        
        // Esperar un poco antes de la última llamada
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Obtener distribución de cesiones (ÚLTIMA LLAMADA)
        console.log('Intentando obtener cesiones distribución...');
        const distribucionData = await getCesionesDistribucion(user);
        console.log('Distribución data recibida:', distribucionData);
        
        if (isMounted) {
          setCesionesDistribucion(distribucionData);
          setDistribucionLoading(false);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          setLoading(false);
          setChartLoading(false);
          setCreditoLeasingLoading(false);
          setTareasLoading(false);
          setResumenLoading(false);
          setDistribucionLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [user]);

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Inicio</h1>
        <p className="text-gray-600">Resumen de gestión comercial</p>
      </div>
      
      {/* Tarjetas de métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="seccion-datos">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdAttachMoney className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Volumen Factoring</p>
              <p className="text-2xl font-semibold text-gray-800">
                {loading ? (
                  <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-blue-400" />
                ) : formatNumber(inicioData?.ANTICIPADO)}
              </p>
            </div>
          </div>
        </div>

        <div className="seccion-datos">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <MdTrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Meta Factoring</p>
              <p className="text-2xl font-semibold text-gray-800">
                {loading ? (
                  <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-green-400" />
                ) : formatNumber(inicioData?.META_MET_VOL)}
              </p>
            </div>
          </div>
        </div>

        <div className="seccion-datos">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MdShowChart className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Proyección Factoring</p>
              <p className="text-2xl font-semibold text-gray-800">
                {loading ? (
                  <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-purple-400" />
                ) : formatNumber(inicioData?.PROYECCION)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tarjetas de Crédito/Leasing y Tareas Pendientes */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Columna izquierda: Crédito y Leasing */}
        <div className="space-y-8">
          {/* Tarjeta de Crédito */}
          <div className="seccion-datos">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MdCreditCard className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-xl font-medium text-gray-600">Crédito</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Créditos Cursados</p>
                <p className="text-xl font-semibold text-gray-800">
                  {creditoLeasingLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-orange-400" />
                  ) : formatNumber(creditoLeasingData?.MONTO_PAGARE)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Meta Crédito</p>
                <p className="text-xl font-semibold text-gray-800">
                  {creditoLeasingLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-orange-400" />
                  ) : formatNumber(creditoLeasingData?.METAS_CREDITO)}
                </p>
              </div>
            </div>
          </div>

          {/* Tarjeta de Leasing */}
          <div className="seccion-datos">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <MdDirectionsCar className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-xl font-medium text-gray-600">Leasing</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Leasing Cursado</p>
                <p className="text-xl font-semibold text-gray-800">
                  {creditoLeasingLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-indigo-400" />
                  ) : formatNumber(creditoLeasingData?.LEASING)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Meta Leasing</p>
                <p className="text-xl font-semibold text-gray-800">
                  {creditoLeasingLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-indigo-400" />
                  ) : formatNumber(creditoLeasingData?.METAS_LEASING)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha: Tareas Pendientes */}
        <div className="seccion-datos h-full">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <MdAssignment className="w-6 h-6 text-yellow-600" />
            </div>
              <div className="ml-4">
                <p className="text-xl font-medium text-gray-600">Tareas Pendientes</p>
              </div>
          </div>
          {tareasLoading ? (
            <div className="flex justify-center items-center h-32">
              <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-gray-400" />
            </div>
          ) : (
            <div className="space-y-0.5">
              {tareasPendientes.length > 0 && (
                <>
                  <div className={`flex justify-between items-center py-2 px-3 rounded border-b border-gray-600 ${
                    parseInt(tareasPendientes[0]?.CAMP_SIN_GESTION || '0') > 0 ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <Link 
                      to="/campanas"
                      className={`text-sm font-medium hover:underline ${
                        parseInt(tareasPendientes[0]?.CAMP_SIN_GESTION || '0') > 0 ? 'text-red-800' : 'text-green-800'
                      }`}
                    >
                      Campaña: Sin gestión
                    </Link>
                    <span className={`font-semibold ${
                      parseInt(tareasPendientes[0]?.CAMP_SIN_GESTION || '0') > 0 ? 'text-red-900' : 'text-green-900'
                    }`}>{tareasPendientes[0]?.CAMP_SIN_GESTION || '0'}</span>
                  </div>
                  <div className={`flex justify-between items-center py-2 px-3 rounded border-b border-gray-600 ${
                    parseInt(tareasPendientes[0]?.CAMP_VOLVER_LLAMAR || '0') > 0 ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <Link 
                      to="/campanas"
                      className={`text-sm font-medium hover:underline ${
                        parseInt(tareasPendientes[0]?.CAMP_VOLVER_LLAMAR || '0') > 0 ? 'text-red-800' : 'text-green-800'
                      }`}
                    >
                      Campaña: Volver a llamar
                    </Link>
                    <span className={`font-semibold ${
                      parseInt(tareasPendientes[0]?.CAMP_VOLVER_LLAMAR || '0') > 0 ? 'text-red-900' : 'text-green-900'
                    }`}>{tareasPendientes[0]?.CAMP_VOLVER_LLAMAR || '0'}</span>
                  </div>
                  <div className={`flex justify-between items-center py-2 px-3 rounded border-b border-gray-600 ${
                    parseInt(tareasPendientes[0]?.BOLSA_DOC_PENDIENTE || '0') > 0 ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <Link 
                      to="/bolsa-documentos"
                      className={`text-sm font-medium hover:underline ${
                        parseInt(tareasPendientes[0]?.BOLSA_DOC_PENDIENTE || '0') > 0 ? 'text-red-800' : 'text-green-800'
                      }`}
                    >
                      Bolsa de Documentos: Pendientes
                    </Link>
                    <span className={`font-semibold ${
                      parseInt(tareasPendientes[0]?.BOLSA_DOC_PENDIENTE || '0') > 0 ? 'text-red-900' : 'text-green-900'
                    }`}>{tareasPendientes[0]?.BOLSA_DOC_PENDIENTE || '0'}</span>
                  </div>
                  <div className={`flex justify-between items-center py-2 px-3 rounded border-b border-gray-600 ${
                    parseInt(tareasPendientes[0]?.LEAD_SIN_GESTION || '0') > 0 ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <Link 
                      to="/en-gestion"
                      className={`text-sm font-medium hover:underline ${
                        parseInt(tareasPendientes[0]?.LEAD_SIN_GESTION || '0') > 0 ? 'text-red-800' : 'text-green-800'
                      }`}
                    >
                      Lead: Sin gestión
                    </Link>
                    <span className={`font-semibold ${
                      parseInt(tareasPendientes[0]?.LEAD_SIN_GESTION || '0') > 0 ? 'text-red-900' : 'text-green-900'
                    }`}>{tareasPendientes[0]?.LEAD_SIN_GESTION || '0'}</span>
                  </div>
                  <div className={`flex justify-between items-center py-2 px-3 rounded ${
                    parseInt(tareasPendientes[0]?.LEAD_VOLVER_LLAMAR || '0') > 0 ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <Link 
                      to="/en-gestion"
                      className={`text-sm font-medium hover:underline ${
                        parseInt(tareasPendientes[0]?.LEAD_VOLVER_LLAMAR || '0') > 0 ? 'text-red-800' : 'text-green-800'
                      }`}
                    >
                      Lead: Volver a llamar
                    </Link>
                    <span className={`font-semibold ${
                      parseInt(tareasPendientes[0]?.LEAD_VOLVER_LLAMAR || '0') > 0 ? 'text-red-900' : 'text-green-900'
                    }`}>{tareasPendientes[0]?.LEAD_VOLVER_LLAMAR || '0'}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Gráfico y Tarjetas de Resumen */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Gráfico de Área - 3 columnas */}
        <div className="lg:col-span-3 seccion-datos">
          <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>Detalle de Cesiones de los últimos 30 días</h3>
          {chartLoading ? (
            <div className="flex justify-center items-center h-64">
              <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--if-gray-300)" />
                <XAxis 
                  dataKey="FECHA" 
                  tick={{ fontSize: 10, fill: 'var(--if-text-secondary)', angle: -45, textAnchor: 'end' }}
                  axisLine={{ stroke: 'var(--if-gray-400)' }}
                  tickLine={{ stroke: 'var(--if-gray-400)' }}
                  height={60}
                  interval={0}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: 'var(--if-text-secondary)' }}
                  axisLine={{ stroke: 'var(--if-gray-400)' }}
                  tickLine={{ stroke: 'var(--if-gray-400)' }}
                  tickFormatter={(value) => value.toLocaleString('es-CL')}
                />
                <Tooltip 
                  formatter={(value) => [`$${value.toLocaleString('es-CL')}`, '']}
                  labelFormatter={(label) => `Fecha: ${label}`}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid var(--if-gray-300)', color: 'var(--if-text-primary)' }}
                />
                <Legend 
                  wrapperStyle={{ color: 'var(--if-text-primary)' }}
                  align="right"
                  verticalAlign="top"
                  iconType="rect"
                />
                <Area 
                  type="linear" 
                  dataKey="CED_IF" 
                  stackId="1" 
                  stroke="var(--if-accent)" 
                  fill="var(--if-accent)" 
                  fillOpacity={0.6}
                  name="Interfactor"
                />
                <Area 
                  type="linear" 
                  dataKey="CED_OTRO" 
                  stackId="1" 
                  stroke="var(--if-gray-400)" 
                  fill="var(--if-gray-400)" 
                  fillOpacity={0.6}
                  name="Cesiones a Otros"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Tarjetas de Resumen - 1 columna */}
        <div className="h-full flex flex-col justify-evenly">
          {/* Facturación en el periodo */}
          <div className="seccion-datos">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <MdReceipt className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Facturación en el periodo</p>
                <p className="text-2xl font-semibold text-gray-800">
                  {resumenLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-blue-400" />
                  ) : formatNumber(resumenData?.FACTURCION)}
                </p>
              </div>
            </div>
          </div>

          {/* Cedidas a Interfactor */}
          <div className="seccion-datos">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <MdBusiness className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cedidas a Interfactor</p>
                <p className="text-2xl font-semibold text-gray-800">
                  {resumenLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-green-400" />
                  ) : formatNumber(resumenData?.CED_IF)}
                </p>
                <p className="text-xs mt-1" style={{color: 'var(--if-text-muted)'}}>
                  {resumenLoading ? '' : `${resumenData?.PER_CED_IF?.toFixed(1) || '0'}% del total de cesiones del periodo`}
                </p>
              </div>
            </div>
          </div>

          {/* Cedidas a Otros */}
          <div className="seccion-datos">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <MdSwapHoriz className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cedidas a Otros</p>
                <p className="text-2xl font-semibold text-gray-800">
                  {resumenLoading ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-purple-400" />
                  ) : formatNumber(resumenData?.CED_OTRO)}
                </p>
                <p className="text-xs mt-1" style={{color: 'var(--if-text-muted)'}}>
                  {resumenLoading ? '' : `${resumenData?.PER_CED_OTRO?.toFixed(1) || '0'}% del total de cesiones del periodo`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Distribución de Cesiones */}
      <div className="mt-8 seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
            Distribución de Cesiones 
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({cesionesDistribucion.length} registros)
            </span>
          </h3>
          {cesionesDistribucion.length > 0 && (
            <button
              onClick={() => exportToExcel()}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {cesionesDistribucion.length > 0 && !distribucionLoading && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={distribucionSearchTerm} onChange={(e) => setDistribucionSearchTerm(e.target.value)} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {distribucionSearchTerm && <button type="button" onClick={() => setDistribucionSearchTerm('')} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {distribucionSearchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedDistribucion.length === 0 ? <span>No se encontraron resultados para &quot;{distribucionSearchTerm}&quot;</span> : <span>{sortedDistribucion.length} resultado{sortedDistribucion.length !== 1 ? 's' : ''} encontrado{sortedDistribucion.length !== cesionesDistribucion.length ? ` de ${cesionesDistribucion.length} registros` : ''}</span>}</div>}
          </div>
        )}
        {distribucionLoading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : cesionesDistribucion.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : sortedDistribucion.length === 0 && distribucionSearchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{distribucionSearchTerm}&quot;</div>
        ) : (
          <div className="tabla-container">
            <table>
              <thead>
                <tr>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleDistribucionSort('CESIONARIO')}>Rut Factoring {getDistribucionSortIcon('CESIONARIO')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleDistribucionSort('FACTORING')}>Factoring {getDistribucionSortIcon('FACTORING')}</th>
                  <th style={{textAlign: 'right', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleDistribucionSort('MTO_CESION_MILL')}>Monto de Cesiones {getDistribucionSortIcon('MTO_CESION_MILL')}</th>
                  <th style={{textAlign: 'right', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleDistribucionSort('PORCENTAJE_TOTAL')}>Porcentaje del Total {getDistribucionSortIcon('PORCENTAJE_TOTAL')}</th>
                  <th style={{textAlign: 'center'}}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {sortedDistribucion.map((item, index) => (
                  <tr key={index}>
                    <td>{item.CESIONARIO}</td>
                    <td>{item.FACTORING}</td>
                    <td style={{textAlign: 'right'}}>
                      {formatNumber(item.MTO_CESION_MILL)}
                    </td>
                    <td style={{textAlign: 'right'}}>
                      {(item.PORCENTAJE_TOTAL * 100).toFixed(0)}%
                    </td>
                    <td style={{textAlign: 'center'}}>
                      <button
                        onClick={() => navigate('/detalle-cesion', { state: { cesionario: item.CESIONARIO } })}
                        className="btn-primary"
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          borderRadius: '4px'
                        }}
                      >
                        <MdVisibility className="w-3 h-3 mr-1" style={{display: 'inline'}} />
                        Ver Detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Inicio